import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Package, AlertCircle, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import api from '@/services/api';

interface AddAssetModalProps {
  open: boolean;
  onClose: () => void;
  formId: string;
  onSuccess: () => void;
}

interface Asset {
  id: string;
  name: string;
  assetTag: string;
  serialNumber?: string;
  status: string;
  category?: { name: string };
  brand?: { name: string };
}

const CONDITION_OPTIONS = [
  { value: 'NEW', label: 'New', description: 'Brand new, never used' },
  { value: 'EXCELLENT', label: 'Excellent', description: 'Like new condition' },
  { value: 'GOOD', label: 'Good', description: 'Minor wear, fully functional' },
  { value: 'FAIR', label: 'Fair', description: 'Some wear, works well' },
  { value: 'POOR', label: 'Poor', description: 'Significant wear, functional' },
];

interface FormData {
  assetId: string;
  condition: string;
  accessories: string;
  notes: string;
}

interface FormErrors {
  assetId?: string;
  condition?: string;
}

export default function AddAssetModal({ open, onClose, formId, onSuccess }: AddAssetModalProps) {
  const queryClient = useQueryClient();
  
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState<FormData>({
    assetId: '',
    condition: 'GOOD',
    accessories: '',
    notes: '',
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Fetch available assets
  const { data: assetsData, isLoading: assetsLoading } = useQuery({
    queryKey: ['availableAssets', search],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('status', 'AVAILABLE');
      params.append('limit', '50');
      if (search) params.append('search', search);
      
      const response = await api.get(`/assets?${params.toString()}`);
      return response.data.data || [];
    },
    enabled: open,
  });

  const assets: Asset[] = assetsData || [];

  // Add asset mutation
  const addAssetMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const payload = {
        assetId: data.assetId,
        condition: data.condition,
        accessories: data.accessories || null,
        notes: data.notes || null,
      };
      const response = await api.post(`/asset-issue-forms/${formId}/assets`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-issue-form', formId] });
      resetForm();
      onSuccess();
    },
    onError: (error: any) => {
      setSubmitError(error.response?.data?.message || 'Failed to add asset');
    },
  });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.assetId) {
      newErrors.assetId = 'Please select an asset';
    }

    if (!formData.condition) {
      newErrors.condition = 'Please select a condition';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    addAssetMutation.mutate(formData);
  };

  const resetForm = () => {
    setFormData({
      assetId: '',
      condition: 'GOOD',
      accessories: '',
      notes: '',
    });
    setSearch('');
    setErrors({});
    setSubmitError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const selectedAsset = assets.find(a => a.id === formData.assetId);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <Card className="border-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Add Asset to Form
              </CardTitle>
              <CardDescription>
                Select an available asset to add to this issue form
              </CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="w-5 h-5" />
            </Button>
          </CardHeader>
          
          <CardContent className="pt-6">
            {/* Error Alert */}
            {submitError && (
              <div className="flex items-center gap-2 p-3 mb-4 rounded-md bg-destructive/10 border border-destructive/20">
                <AlertCircle className="w-5 h-5 text-destructive" />
                <p className="text-sm text-destructive">{submitError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Asset Search & Selection */}
              <div className="space-y-2">
                <Label>
                  Asset <span className="text-destructive">*</span>
                </Label>
                
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, asset tag, or serial number..."
                    className="pl-10"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                {/* Asset List */}
                <div className={cn(
                  "border rounded-md max-h-[200px] overflow-y-auto",
                  errors.assetId ? "border-destructive" : "border-input"
                )}>
                  {assetsLoading ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      Loading assets...
                    </div>
                  ) : assets.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No available assets found
                    </div>
                  ) : (
                    <div className="divide-y">
                      {assets.map((asset) => (
                        <button
                          key={asset.id}
                          type="button"
                          className={cn(
                            "w-full p-3 text-left hover:bg-muted/50 transition-colors",
                            formData.assetId === asset.id && "bg-primary/10 border-l-2 border-l-primary"
                          )}
                          onClick={() => {
                            setFormData({ ...formData, assetId: asset.id });
                            if (errors.assetId) {
                              setErrors(prev => ({ ...prev, assetId: undefined }));
                            }
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{asset.name}</p>
                              <p className="text-sm text-muted-foreground">
                                <span className="font-mono">{asset.assetTag}</span>
                                {asset.serialNumber && <span> · S/N: {asset.serialNumber}</span>}
                              </p>
                            </div>
                            <div className="text-right text-sm text-muted-foreground">
                              {asset.category?.name && <p>{asset.category.name}</p>}
                              {asset.brand?.name && <p>{asset.brand.name}</p>}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {errors.assetId && (
                  <p className="text-sm text-destructive">{errors.assetId}</p>
                )}
                
                {selectedAsset && (
                  <div className="p-3 rounded-md bg-primary/5 border border-primary/20">
                    <p className="text-sm font-medium text-primary">Selected: {selectedAsset.name}</p>
                    <p className="text-xs text-muted-foreground">{selectedAsset.assetTag}</p>
                  </div>
                )}
              </div>

              {/* Condition */}
              <div className="space-y-2">
                <Label>
                  Condition at Issue <span className="text-destructive">*</span>
                </Label>
                <div className="grid gap-2 sm:grid-cols-5">
                  {CONDITION_OPTIONS.map((condition) => (
                    <button
                      key={condition.value}
                      type="button"
                      className={cn(
                        "p-2 rounded-lg border-2 transition-all text-center",
                        formData.condition === condition.value
                          ? "border-primary bg-primary/5"
                          : "border-input hover:border-muted-foreground/50"
                      )}
                      onClick={() => {
                        setFormData({ ...formData, condition: condition.value });
                        if (errors.condition) {
                          setErrors(prev => ({ ...prev, condition: undefined }));
                        }
                      }}
                    >
                      <p className="font-medium text-sm">{condition.label}</p>
                    </button>
                  ))}
                </div>
                {errors.condition && (
                  <p className="text-sm text-destructive">{errors.condition}</p>
                )}
              </div>

              {/* Accessories */}
              <div className="space-y-2">
                <Label htmlFor="accessories">Accessories Included</Label>
                <Input
                  id="accessories"
                  placeholder="e.g., Charger, Mouse, Bag, Keyboard..."
                  value={formData.accessories}
                  onChange={(e) => setFormData({ ...formData, accessories: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  List any accessories being issued with this asset
                </p>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <textarea
                  id="notes"
                  className="w-full min-h-[80px] p-3 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Any additional notes about this asset..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={addAssetMutation.isPending}>
                  {addAssetMutation.isPending ? 'Adding...' : 'Add Asset'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
